import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { redirectToSsoLogin, ssoLogout } from 'auth/ssoClient'
import {
  AI_INSIGHTS_URL,
  AiInsightsError,
  askAiInsights,
  fetchAiCreditInfo,
  signInWasAlreadyRejected,
  type AiCreditInfo,
  type AiInsights,
} from 'brief/aiInsights'
import { CHANNEL_LABEL, type Brief, type NextStep, type TimelineEntry } from 'brief/buildBrief'
import { formatWhen, relativeDay } from 'brief/parseWhen'
import { suggestNextSteps } from 'brief/suggestNextSteps'
import { MemoryError, savePending } from 'brief/memoryApi'
import { memoryHeadline, openCount } from 'brief/memoryView'
import { useRecordMemory } from 'brief/useRecordMemory'
import { MemorySection } from './MemorySection'
import type { ChannelProgress } from 'brief/useEntityBrief'
import type { ActionWidgetType } from 'types/entity'

/**
 * Insights - the first thing on the record.
 *
 * Whoever opens this customer, for whatever reason (their own follow-up, a colleague's lead they
 * have inherited, a call that just landed on them), reads the same answers without a click: what
 * needs doing, when we last spoke, what was last written down, what is planned, who has handled
 * this before - and then what to do about it, with the button that does it. Each answer names the
 * entry it came from, so it can be checked in the timeline below instead of taken on faith.
 *
 * Everything here is free and worked out from the record, except the one row marked with the AI
 * glyph, which says what it costs before it is pressed.
 */

/**
 * What a step's button says - the form it opens, never just "Open". There is no dial button on
 * this screen, so a step about phoning the customer leads to the follow-up form, where the call is
 * logged; the label has to say that, or "call them" next to a button that opens a form reads as a
 * mistake.
 */
const ACTION_LABEL: Partial<Record<ActionWidgetType, string>> = {
  followup: 'Log follow-up',
  whatsapp: 'Open WhatsApp',
  email: 'Write email',
  sms: 'Write SMS',
  tasks: 'Add task',
  appointment: 'Add appointment',
  notes: 'Add note',
}

/**
 * The "New" mark beside the title. It removes itself after this date, so nobody has to remember
 * to take it off - a "new" badge that is a year old tells users the opposite of what it says.
 */
const NEW_UNTIL = new Date(2026, 11, 31) // 31 Dec 2026
const IS_NEW = new Date() < NEW_UNTIL

const STEP_LABEL: Record<NextStep['kind'], string> = {
  followup: 'Follow-up',
  task: 'Task',
  appointment: 'Appointment',
}

function Source({ entry, now }: { entry: TimelineEntry; now: Date }) {
  const parts = [CHANNEL_LABEL[entry.channel]]
  if (entry.who) parts.push(entry.who)
  if (entry.when) parts.push(`${relativeDay(entry.when, now)} · ${formatWhen(entry.when, entry.hasTime)}`)
  return <span className="brief-source">{parts.join(' · ')}</span>
}

function Block({ label, tone, children }: { label: string; tone?: 'danger'; children: ReactNode }) {
  return (
    <div className={`brief-block${tone ? ` is-${tone}` : ''}`}>
      <span className="brief-label">{label}</span>
      {children}
    </div>
  )
}

function Quote({ text }: { text: string }) {
  return <p className="brief-quote">{text}</p>
}

function StepLine({ step, now }: { step: NextStep; now: Date }) {
  const late = step.overdueDays > 0
  const state = late
    ? `overdue ${step.overdueDays} day${step.overdueDays === 1 ? '' : 's'}`
    : relativeDay(step.when, now)
  return (
    <li>
      <span className={`brief-pill ${late ? 'is-danger' : step.dueToday ? 'is-warn' : 'is-good'}`}>{state}</span>
      <span className="brief-step-what">
        {STEP_LABEL[step.kind]}
        {step.kind !== 'followup' && step.label ? `: ${step.label}` : step.label ? ` (${step.label})` : ''}
      </span>
      <span className="brief-source">
        {formatWhen(step.when, step.hasTime)}
        {step.who ? ` · ${step.who}` : ''}
      </span>
    </li>
  )
}

/**
 * A yes/no the panel remembers for this person on this browser - how much of Insights they want
 * in front of them. Storage can be blocked or full; the panel then simply uses the default.
 */
function useRemembered(key: string, fallback: boolean): [boolean, (next: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(key)
      return saved === null ? fallback : saved === '1'
    } catch {
      return fallback
    }
  })
  const update = (next: boolean) => {
    setValue(next)
    try {
      localStorage.setItem(key, next ? '1' : '0')
    } catch {
      /* not remembered this time - nothing else depends on it */
    }
  }
  return [value, update]
}

/**
 * What the wait says. Each line is something the panel is genuinely doing, so reading them is
 * learning what Insights is - and by the time two have gone by, it has usually loaded. The order
 * starts somewhere different each time so it never reads as a canned sequence.
 */
const LOADING_LINES = [
  'Listening back through the calls…',
  'Reading the WhatsApp thread, both sides…',
  'Checking what was promised, and by when…',
  'Looking for anything the customer asked that nobody answered…',
  'Seeing who on the team has spoken to them before…',
  'Finding the last thing anyone wrote down…',
  'Putting every conversation in one order…',
  'Working out what matters most right now…',
]
const LINE_MS = 1700

function InsightsLoader({ progress }: { progress: ChannelProgress[] }) {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * LOADING_LINES.length))
  useEffect(() => {
    const timer = window.setInterval(() => setIndex((i) => (i + 1) % LOADING_LINES.length), LINE_MS)
    return () => window.clearInterval(timer)
  }, [])

  const done = progress.filter((c) => c.done).length
  return (
    <div className="insights-loader" role="status" aria-live="polite">
      {/* keyed so each new line plays the entrance animation */}
      <p key={index} className="insights-loader-line">{LOADING_LINES[index]}</p>
      {progress.length > 0 && (
        <div className="insights-loader-channels" aria-label={`${done} of ${progress.length} read`}>
          {progress.map((c) => (
            <span key={c.key} className={`insights-loader-chip${c.done ? ' is-done' : ''}`}>
              <span className="insights-loader-dot" aria-hidden="true" />
              {c.label}
            </span>
          ))}
        </div>
      )}
      <div className="insights-loader-bar" aria-hidden="true">
        <span style={{ width: `${progress.length ? Math.max(8, (done / progress.length) * 100) : 8}%` }} />
      </div>
    </div>
  )
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg className={`chev${open ? ' is-open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="9 6 15 12 9 18" />
    </svg>
  )
}

function InsightsGlyph() {
  return (
    <svg className="insights-glyph" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 18h6" />
      <path d="M10 22h4" />
      <path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2V17h6v-.3c0-.8.4-1.500 1-2A7 7 0 0 0 12 2z" />
    </svg>
  )
}

/** The mark that means "this part is AI, and AI is billed". Used nowhere else on the panel. */
function AiGlyph() {
  return (
    <svg className="ai-glyph" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2l1.9 5.6a3 3 0 0 0 1.9 1.900L21.400 11.400l-5.600 1.900a3 3 0 0 0-1.900 1.900L12 20.800l-1.900-5.600a3 3 0 0 0-1.900-1.900L2.600 11.400l5.600-1.900a3 3 0 0 0 1.900-1.900z" />
      <path d="M19 2l.7 2.100L21.800 4.800l-2.100.7L19 7.600l-.7-2.100-2.100-.700 2.100-.7z" />
    </svg>
  )
}

/**
 * "How is this charged?" - opens on hover, on keyboard focus, and on tap (a tooltip that only
 * answers a mouse is no answer on a phone). Escape or a click elsewhere closes it.
 */
const money = (value: number) => `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

function ChargeInfo() {
  const [open, setOpen] = useState(false)
  const id = useId()
  const wrap = useRef<HTMLSpanElement>(null)
  // The account's live rate - asked for the first time the tooltip opens, never typed in here.
  const [credit, setCredit] = useState<AiCreditInfo | null | undefined>(undefined)
  useEffect(() => {
    if (!open || credit !== undefined) return
    const controller = new AbortController()
    fetchAiCreditInfo(controller.signal).then(setCredit)
    return () => controller.abort()
  }, [open, credit])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDown)
    }
  }, [open])

  return (
    <span className="charge-info" ref={wrap} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        className="charge-info-btn"
        aria-label="How AI suggestions are charged"
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        i
      </button>
      {open && (
        <span role="tooltip" id={id} className="charge-info-tip">
          <strong>How this is charged</strong>
          <span>Everything else on this panel is free — it is worked out from the record itself.</span>
          <span>
            The AI read is billed to your wallet as AI credits, and only when you press the button. It never runs on
            its own.
          </span>
          {credit && !credit.billingEnabled ? (
            <span>AI reads are not being charged on this account at the moment.</span>
          ) : credit && credit.pricePerCredit > 0 ? (
            <>
              <span>
                Your rate: 1 credit = {money(credit.pricePerCredit)}, covering about{' '}
                {Math.round(credit.unitsPerCredit).toLocaleString('en-IN')} tokens of reading and writing. One read
                costs at most {credit.estCreditsPerAsk} credit{credit.estCreditsPerAsk === 1 ? '' : 's'} (
                {money(credit.estInrPerAsk)}) — usually less, as it depends on how much history the record has.
              </span>
              {credit.balanceAvailable && <span>Wallet balance now: {money(credit.balance)}.</span>}
            </>
          ) : (
            <span>The cost depends on how much history the record has.</span>
          )}
          <span>
            The exact credits used are shown with every answer, and if the wallet is short nothing runs and nothing is
            charged.
          </span>
        </span>
      )}
    </span>
  )
}

export function BriefPanel({
  brief,
  loading,
  progress,
  stillReading,
  asOf,
  refreshing,
  personName,
  entityType,
  entityId,
  onReload,
  canAct,
  onAct,
}: {
  brief: Brief | null
  loading: boolean
  progress: ChannelProgress[]
  /** Channels that have not answered yet; what is shown does not include them. */
  stillReading: string[]
  /** When what is shown was read; null until anything is. */
  asOf: Date | null
  /** A stored snapshot is on screen while the record is re-read behind it. */
  refreshing: boolean
  personName: string
  entityType: 'lead' | 'enquiry'
  entityId: string
  onReload: () => void
  /** Whether this user may open that action here (role permission, enquiry limits). */
  canAct: (action: ActionWidgetType) => boolean
  /** Opens the action panel on that form. */
  onAct: (action: ActionWidgetType) => void
}) {
  const now = new Date()
  const [ai, setAi] = useState<AiInsights | null>(null)
  const [aiBusy, setAiBusy] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)
  const [aiSignedOut, setAiSignedOut] = useState(false)
  // How much of the panel this person wants in front of them. It opens compact - the headline and
  // two closed sections - so the customer's own card is on screen when the page lands.
  const [minimised, setMinimised] = useRemembered('insights:minimised', false)
  const [factsOpen, setFactsOpen] = useRemembered('insights:facts', false)
  const [stepsOpen, setStepsOpen] = useRemembered('insights:steps', false)
  const [memoryOpen, setMemoryOpen] = useRemembered('insights:memory', false)
  const factsId = useId()
  const stepsId = useId()
  const memoryId = useId()
  // What stays true about this customer between conversations. Loaded beside the brief, never in
  // its way: if it cannot be read the rest of the panel is untouched.
  const mem = useRecordMemory(entityType, entityId, true)
  const [savingPending, setSavingPending] = useState(false)

  // A different record, or the same one after a reload, makes the last AI answer stale.
  useEffect(() => {
    setAi(null)
    setAiError(null)
    setAiSignedOut(false)
  }, [entityId, brief])

  const ready = !loading && !!brief
  const aiOffered = !!AI_INSIGHTS_URL || process.env.NODE_ENV === 'development'
  const aiDisabled = !AI_INSIGHTS_URL || aiBusy || !ready || stillReading.length > 0 || refreshing

  const runAi = async () => {
    if (!brief) return
    // The answer appears inside the panel, so a press from the docked bar opens it first.
    setMinimised(false)
    setAiBusy(true)
    setAiError(null)
    setAiSignedOut(false)
    try {
      const answer = await askAiInsights(entityType, entityId, brief, mem.offered && !mem.loading && !mem.error ? mem.memory : null)
      setAi(answer)
      if (answer.memory?.saved) mem.reload()
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        setAiSignedOut(err instanceof AiInsightsError && err.kind === 'signed-out')
        setAiError(err instanceof AiInsightsError ? err.message : 'The AI read did not finish. Nothing was charged.')
      }
    } finally {
      setAiBusy(false)
    }
  }

  // The sign-in page hands back the token it has cached for as long as ITS check passes - a token
  // minted at 13:57 came back from a sign-in at 20:21. So once a server has refused the token now
  // held, another trip to that page changes nothing; only a full sign-out makes it mint a new one.
  // Other Kit19 tabs hold the same refused token, so nothing that worked is lost by signing out.
  const signInAgain = () => {
    const here = window.location.pathname + window.location.search
    if (signInWasAlreadyRejected()) void ssoLogout(window.location.origin + here)
    else redirectToSsoLogin(here)
  }

  // The read was paid for but its memory update was not stored: store it now, from what came back.
  const saveToMemory = async () => {
    if (!ai?.memory) return
    setSavingPending(true)
    try {
      await savePending(entityType, entityId, ai.memory.pendingOps, ai.memory.summary, ai.memory.pendingReadTo)
      // the note then reports what was stored, from the operations that were just saved
      const count = (op: string) => ai.memory!.pendingOps.filter((o) => o.op === op).length
      setAi({ ...ai, memory: { ...ai.memory, saved: true, added: count('add'), closed: count('close'), reworded: count('reword'), pendingOps: [], error: null } })
      mem.reload()
    } catch (err) {
      setAi({ ...ai, memory: { ...ai.memory, error: err instanceof MemoryError ? err.message : 'It could not be saved.' } })
    } finally {
      setSavingPending(false)
    }
  }

  const aiButton = (compact: boolean) =>
    aiOffered && (
      <button
        type="button"
        className={`btn btn-sm ai-btn${compact ? ' is-icon' : ''}`}
        onClick={runAi}
        disabled={aiDisabled}
        title={
          !AI_INSIGHTS_URL
            ? 'Preview — the AI service is not connected in this build'
            : stillReading.length > 0
              ? 'Available once the whole record has been read'
              : 'Ask AI what to do and say next — billed to your wallet as AI credits'
        }
        aria-label="Ask AI what to do and say next"
      >
        <AiGlyph />
        {!compact && (aiBusy ? 'Reading the record…' : ai ? 'Ask AI again' : 'Ask AI what to do and say next')}
      </button>
    )
  const header = (
    <header className="brief-head">
      <div className="brief-title">
        <InsightsGlyph />
        <div className="brief-title-text">
          <h4>
            Insights
            {IS_NEW && <span className="brief-new" title="Insights is new on this screen">New</span>}
          </h4>
          {minimised && ready && brief ? (
            <span className={`brief-sub brief-sub-headline is-${brief.headline.tone}`}>{brief.headline.text}</span>
          ) : (
            <span className="brief-sub">{personName ? `Before you talk to ${personName}` : 'Before you talk'}</span>
          )}
        </div>
      </div>
      <div className="brief-head-actions">
        {minimised && ready && aiButton(true)}
        {ready && !minimised && (
          <button type="button" className="btn btn-icon" onClick={onReload} title="Read the record again">
            ⟳
          </button>
        )}
        <button
          type="button"
          className="btn btn-icon"
          onClick={() => setMinimised(!minimised)}
          aria-expanded={!minimised}
          title={minimised ? 'Show Insights' : 'Minimise Insights'}
        >
          <span className={`chev-v${minimised ? '' : ' is-open'}`} aria-hidden="true">⌄</span>
        </button>
      </div>
    </header>
  )

  if (loading || !brief) {
    return (
      <section className={`card brief-panel${minimised ? ' is-min' : ''}`} aria-busy="true">
        {header}
        {!minimised && <InsightsLoader progress={progress} />}
      </section>
    )
  }

  const { lastContact, lastWords, latestNote, waitingOnUs, nextSteps, people, newToYou, unavailable } = brief
  const others = people.filter((p) => !p.isYou)
  const suggestions = suggestNextSteps(brief, now)
  const memoryLine = mem.memory ? memoryHeadline(mem.memory.items, now) : null

  const ActButton = ({ action, label }: { action?: ActionWidgetType; label?: string }) =>
    action && canAct(action) ? (
      <button type="button" className="btn btn-sm suggest-act" onClick={() => onAct(action)}>
        {label ?? 'Open'}
      </button>
    ) : null

  if (minimised) {
    return <section className={`card brief-panel is-min tone-${brief.headline.tone}`}>{header}</section>
  }

  return (
    <section className={`card brief-panel tone-${brief.headline.tone}`}>
      {header}

      <p className={`brief-headline is-${brief.headline.tone}`}>{brief.headline.text}</p>
      {memoryLine && <p className={`brief-headline is-${memoryLine.tone} is-second`}>{memoryLine.text}</p>}

      <div className="brief-toggles">
        <button type="button" className={`brief-toggle${factsOpen ? ' is-on' : ''}`} aria-expanded={factsOpen} aria-controls={factsId} onClick={() => setFactsOpen(!factsOpen)}>
          <Chevron open={factsOpen} />
          What we know
        </button>
        <button type="button" className={`brief-toggle${stepsOpen ? ' is-on' : ''}`} aria-expanded={stepsOpen} aria-controls={stepsId} onClick={() => setStepsOpen(!stepsOpen)}>
          <Chevron open={stepsOpen} />
          Suggested next steps
          <span className="brief-toggle-count">{suggestions.length}</span>
        </button>
        {mem.offered && (
          <button type="button" className={`brief-toggle${memoryOpen ? ' is-on' : ''}`} aria-expanded={memoryOpen} aria-controls={memoryId} onClick={() => setMemoryOpen(!memoryOpen)}>
            <Chevron open={memoryOpen} />
            Memory
            {mem.memory && <span className="brief-toggle-count">{openCount(mem.memory.items)}</span>}
          </button>
        )}
        {aiOffered && (
          <span className="brief-ai-slot">
            {aiButton(false)}
            <ChargeInfo />
          </span>
        )}
        {stillReading.length > 0 ? (
          <span className="brief-still" role="status">
            <span className="insights-loader-dot" aria-hidden="true" />
            Still reading {stillReading.join(' and ')} — this may update
          </span>
        ) : refreshing ? (
          <span className="brief-still" role="status">
            <span className="insights-loader-dot" aria-hidden="true" />
            As of {asOf ? formatWhen(asOf, true) : 'the last read'} — checking for changes…
          </span>
        ) : asOf ? (
          <span className="brief-still brief-asof" title="When this record's history was last read">
            As of {formatWhen(asOf, true)}
          </span>
        ) : null}
      </div>

      {(aiError || ai) && (
        <div className="suggest-ai">
          {aiError && (
            <p className="brief-unavailable ai-error">
              {aiError}
              {aiSignedOut && (
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={signInAgain}
                >
                  Sign in again
                </button>
              )}
            </p>
          )}
          {ai && (
            <div className="ai-answer">
              {ai.steps.length > 0 && (
                <ol className="suggest-list">
                  {ai.steps.map((s, i) => (
                    <li key={i}>
                      <div className="suggest-body">
                        <span className="suggest-text">
                          <AiGlyph /> {s.text}
                        </span>
                        {s.why && <span className="brief-source">{s.why}</span>}
                      </div>
                      <ActButton action={s.action} label={s.action ? ACTION_LABEL[s.action] : undefined} />
                    </li>
                  ))}
                </ol>
              )}
              {ai.draft && (
                <div className="ai-draft">
                  <span className="brief-label">Draft {ai.draft.channel} message — check it before you send</span>
                  <p>{ai.draft.text}</p>
                </div>
              )}
              <span className="brief-source">
                Written by AI from this record's history — check it against the timeline. Used {ai.creditsUsed} AI credit
                {ai.creditsUsed === 1 ? '' : 's'}
                {typeof ai.balance === 'number' ? ` · wallet balance ${money(ai.balance)}` : ''}.
              </span>
              {ai.memory && (
                <span className="brief-source ai-memory-note">
                  {ai.memory.saved ? (
                    ai.memory.added + ai.memory.closed + ai.memory.reworded > 0 ? (
                      <>
                        Memory updated in the same read —{' '}
                        {[
                          ai.memory.added ? `${ai.memory.added} added` : '',
                          ai.memory.closed ? `${ai.memory.closed} closed` : '',
                          ai.memory.reworded ? `${ai.memory.reworded} reworded` : '',
                        ].filter(Boolean).join(', ')}
                        .{' '}
                        <button type="button" className="k-linkish" onClick={() => setMemoryOpen(true)}>
                          Open Memory
                        </button>
                      </>
                    ) : (
                      'Memory checked — nothing new worth keeping.'
                    )
                  ) : ai.memory.pendingOps.length > 0 ? (
                    <>
                      The memory update from this read could not be stored{ai.memory.error ? ` (${ai.memory.error})` : ''}. You have
                      already paid for it —{' '}
                      <button type="button" className="k-linkish" disabled={savingPending} onClick={saveToMemory}>
                        {savingPending ? 'saving…' : 'save it to Memory'}
                      </button>
                      .
                    </>
                  ) : ai.memory.error ? (
                    `Memory was not updated: ${ai.memory.error}`
                  ) : null}
                  {ai.memory.rejected > 0 && ` ${ai.memory.rejected} suggestion${ai.memory.rejected === 1 ? ' was' : 's were'} left out because the history did not back ${ai.memory.rejected === 1 ? 'it' : 'them'} up.`}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {factsOpen && (
      <div className="brief-grid" id={factsId}>
        {waitingOnUs && (
          <Block label="Waiting on us" tone="danger">
            <Quote text={waitingOnUs.body || '(message without text)'} />
            <Source entry={waitingOnUs} now={now} />
          </Block>
        )}

        <Block label="Last contact">
          {lastContact ? (
            <>
              <p className="brief-value">
                {lastContact.when ? relativeDay(lastContact.when, now) : lastContact.whenText}
                {lastContact.title ? ` — ${lastContact.title}` : ''}
              </p>
              <Source entry={lastContact} now={now} />
            </>
          ) : (
            <p className="brief-none">No call, message or follow-up on record.</p>
          )}
        </Block>

        <Block label="Last written about them">
          {lastWords ? (
            <>
              <Quote text={lastWords.body} />
              <Source entry={lastWords} now={now} />
            </>
          ) : (
            <p className="brief-none">No follow-up remark yet.</p>
          )}
          {latestNote && (
            <>
              <Quote text={latestNote.body} />
              <Source entry={latestNote} now={now} />
            </>
          )}
        </Block>

        <Block label="Planned">
          {nextSteps.length > 0 ? (
            <ul className="brief-steps">
              {nextSteps.slice(0, 4).map((step) => (
                <StepLine key={step.id} step={step} now={now} />
              ))}
              {nextSteps.length > 4 && <li className="brief-source">+{nextSteps.length - 4} more in the Task and Appointment tabs</li>}
            </ul>
          ) : (
            <p className="brief-none">Nothing is planned — no follow-up date, open task or upcoming appointment.</p>
          )}
        </Block>

        {brief.stuckDeals.length > 0 && (
          <Block label="Pipeline" tone="danger">
            <ul className="brief-steps">
              {brief.stuckDeals.slice(0, 3).map((d) => (
                <li key={d.pipeline}>
                  <span className={`brief-pill ${d.days >= 30 ? 'is-danger' : 'is-warn'}`}>{d.days} days</span>
                  <span className="brief-step-what">{d.stage}</span>
                  <span className="brief-source">{d.pipeline} · since {formatWhen(d.since, false)}</span>
                </li>
              ))}
            </ul>
          </Block>
        )}

        <Block label="Who has handled this">
          {people.length > 0 ? (
            <>
              <div className="brief-people">
                {people.slice(0, 6).map((p) => (
                  <span key={p.name} className={`brief-person${p.isYou ? ' is-you' : ''}`} title={p.last ? `Last: ${formatWhen(p.last, false)}` : undefined}>
                    {p.isYou ? 'You' : p.name} · {p.touches}
                  </span>
                ))}
              </div>
              {newToYou && (
                <p className="brief-note">
                  No entry here is under your name — {others.length === 1 ? `${others[0].name} has` : `${others.length} colleagues have`} been
                  the customer's contact so far.
                </p>
              )}
            </>
          ) : (
            <p className="brief-none">Nobody on the team has logged anything yet.</p>
          )}
        </Block>
      </div>
      )}

      {stepsOpen && (
      <div className="suggest" id={stepsId}>
        {suggestions.length > 0 ? (
          <ol className="suggest-list">
            {suggestions.map((s) => (
              <li key={s.id}>
                <div className="suggest-body">
                  <span className="suggest-text">{s.text}</span>
                  <span className="brief-source">{s.why}</span>
                </div>
                <ActButton action={s.action} label={s.actionLabel} />
              </li>
            ))}
          </ol>
        ) : (
          <p className="brief-none">Nothing is pressing — the record is up to date and the next step is planned.</p>
        )}

      </div>
      )}

      {mem.offered && memoryOpen && (
        <MemorySection
          id={memoryId}
          entityType={entityType}
          entityId={entityId}
          memory={mem.memory}
          loading={mem.loading}
          error={mem.error}
          // the record's own write permission decides: follow-up on a lead; an enquiry has no follow-up, so notes
          canEdit={canAct(entityType === 'lead' ? 'followup' : 'notes')}
          onChanged={mem.reload}
          signedOut={mem.signedOut}
          onSignIn={signInAgain}
        />
      )}

      {unavailable.length > 0 && (
        <p className="brief-unavailable">
          Could not read: {unavailable.join(', ')}. The insights above do not include {unavailable.length === 1 ? 'it' : 'them'} — that is "not known",
          not "none".
        </p>
      )}
    </section>
  )
}
